import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { MapPin, Plus } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { LocationDialog } from '@/components/inventory/LocationDialog';
import {
  LOCATION_KINDS,
  LOCATION_KIND_LABELS,
  useLocations,
  type Location,
  type LocationKind,
} from '@/features/inventory/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatDate } from '@/lib/formatters';

const KIND_OPTIONS = LOCATION_KINDS.map((k) => ({ value: k, label: LOCATION_KIND_LABELS[k] }));

function kindLabel(kind: string | null): string {
  if (!kind) return '—';
  return LOCATION_KIND_LABELS[kind as LocationKind] ?? kind;
}

function formatAddress(loc: Location): string | null {
  const a = loc.address;
  if (!a) return null;
  const parts = [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(', '), a.zip]
    .map((p) => (typeof p === 'string' ? p.trim() : p))
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Location master — the physical / logical buckets on-hand inventory is
 * tracked against (warehouses, the production floor, transit, quarantine).
 * Admins add and edit locations here; every stock movement and on-hand
 * level references one of these rows. Inactive locations stay listed but
 * are filtered out of the movement / adjustment pickers.
 */
export function LocationsPage() {
  const { role } = useAuth();
  const { data, isLoading } = useLocations({ includeInactive: true });
  const [newOpen, setNewOpen] = useState(false);
  const [editLocation, setEditLocation] = useState<Location | null>(null);

  const canManage = canWrite(role, 'inventory');
  const rows = useMemo<Location[]>(() => data ?? [], [data]);

  const columns = useMemo<ColumnDef<Location>[]>(
    () => [
      {
        id: 'code',
        accessorKey: 'code',
        header: 'Code',
        size: 140,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs text-foreground">
            {row.original.code}
          </span>
        ),
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        size: 240,
        minSize: 140,
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.name}</span>
        ),
      },
      {
        id: 'kind',
        accessorFn: (r) => r.kind ?? '',
        header: 'Kind',
        size: 150,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) =>
          row.original.kind ? (
            <Badge variant="secondary">{kindLabel(row.original.kind)}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'address',
        accessorFn: (r) => formatAddress(r) ?? '',
        header: 'Address',
        size: 280,
        minSize: 160,
        enableSorting: false,
        cell: ({ row }) => {
          const addr = formatAddress(row.original);
          return addr ? (
            <span className="text-muted-foreground">{addr}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'is_active',
        accessorFn: (r) => (r.is_active ? 'active' : 'inactive'),
        header: 'Status',
        size: 110,
        minSize: 90,
        filterFn: arrIncludesSome,
        cell: ({ row }) =>
          row.original.is_active ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="muted">Inactive</Badge>
          ),
      },
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: 'Added',
        size: 110,
        minSize: 100,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'kind', title: 'Kind', options: KIND_OPTIONS },
      {
        columnId: 'is_active',
        title: 'Status',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ],
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Locations"
        actions={
          canManage ? (
            <Button onClick={() => setNewOpen(true)}>
              <Plus className="h-4 w-4" />
              Add location
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="pt-6">
          <DataTable<Location, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={canManage ? (row) => setEditLocation(row) : undefined}
            searchPlaceholder="Search by code or name…"
            facetedFilters={facetedFilters}
            initialPageSize={25}
            pageSizes={[10, 25, 50, 100]}
            tableId="inventory-locations"
            mobileCard={{
              title: (row) => row.name,
              subtitle: (row) => <span className="font-mono text-[11px]">{row.code}</span>,
              badges: (row) => [
                row.kind ? (
                  <Badge key="kind" variant="secondary">
                    {kindLabel(row.kind)}
                  </Badge>
                ) : null,
                row.is_active ? (
                  <Badge key="status" variant="success">
                    Active
                  </Badge>
                ) : (
                  <Badge key="status" variant="muted">
                    Inactive
                  </Badge>
                ),
              ],
              meta: (row) => {
                const addr = formatAddress(row);
                return addr ? [{ label: 'Address', value: addr }] : [];
              },
            }}
            emptyState={
              <EmptyState
                icon={MapPin}
                title="No locations yet"
                action={
                  canManage ? (
                    <Button onClick={() => setNewOpen(true)}>
                      <Plus className="h-4 w-4" />
                      Add location
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      <LocationDialog open={newOpen} onOpenChange={setNewOpen} />
      <LocationDialog
        open={editLocation !== null}
        onOpenChange={(open) => {
          if (!open) setEditLocation(null);
        }}
        location={editLocation}
      />
    </div>
  );
}
